import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { RequireAuth, RequireAdmin } from "./components/RouteGuards";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Checkout from "./pages/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import MyProfile from "./pages/MyProfile";
import MyOrders from "./pages/MyOrders";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMockup from "./pages/admin/AdminMockup";
import AdminReports from "./pages/admin/AdminReports";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminProfile from "./pages/admin/AdminProfile";
import AdminCommunity from "./pages/admin/AdminCommunity";
import AdminBulk from "./pages/admin/AdminBulk";
import AdminRestock from "./pages/admin/AdminRestock";
import Community from "./pages/Community";
import CommunityPost from "./pages/CommunityPost";
import Bulk from "./pages/Bulk";
import Affiliate from "./pages/account/Affiliate";
import AdminAffiliates from "./pages/admin/AdminAffiliates";
import AffiliatePop from "./components/AffiliatePop";
import { captureRefFromUrl } from "./lib/affiliate";

function RefCapture() {
  const location = useLocation();
  React.useEffect(() => { captureRefFromUrl(); }, [location.pathname, location.search]);
  // Scroll to top on every page navigation (fixes review-at-top bug from retained scroll)
  React.useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              theme="dark"
              toastOptions={{
                style: {
                  background: "#0A0A0A",
                  border: "1px solid #27272A",
                  color: "#fff",
                  borderRadius: 0,
                  fontFamily: "Space Mono, monospace",
                  fontSize: 12,
                },
              }}
            />
            <RefCapture />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/orders" element={<RequireAuth><MyOrders past={false} /></RequireAuth>} />
              <Route path="/orders/:id" element={<RequireAuth><OrderDetail /></RequireAuth>} />
              <Route path="/account" element={<RequireAuth><MyOrders past={false} /></RequireAuth>} />
              <Route path="/account/orders" element={<RequireAuth><MyOrders past={false} /></RequireAuth>} />
              <Route path="/account/past-orders" element={<RequireAuth><MyOrders past={true} /></RequireAuth>}/>
              <Route path="/account/profile" element={<RequireAuth><MyProfile /></RequireAuth>} />
              <Route path="/account/affiliate" element={<RequireAuth><Affiliate /></RequireAuth>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/community" element={<Community />} />
              <Route path="/community/post/:id" element={<CommunityPost />} />
              <Route path="/bulk" element={<Bulk />} />

              <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
              <Route path="/admin/products" element={<RequireAdmin><AdminProducts /></RequireAdmin>} />
              <Route path="/admin/orders" element={<RequireAdmin><AdminOrders /></RequireAdmin>} />
              <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
              <Route path="/admin/mockup" element={<RequireAdmin><AdminMockup /></RequireAdmin>} />
              <Route path="/admin/reports" element={<RequireAdmin><AdminReports /></RequireAdmin>} />
              <Route path="/admin/payments" element={<RequireAdmin><AdminPayments /></RequireAdmin>} />
              <Route path="/admin/reviews" element={<RequireAdmin><AdminReviews /></RequireAdmin>} />
              <Route path="/admin/profile" element={<RequireAdmin><AdminProfile /></RequireAdmin>} />
              <Route path="/admin/community" element={<RequireAdmin><AdminCommunity /></RequireAdmin>} />
              <Route path="/admin/bulk" element={<RequireAdmin><AdminBulk /></RequireAdmin>} />
              <Route path="/admin/restock" element={<RequireAdmin><AdminRestock /></RequireAdmin>} />
              <Route path="/admin/affiliates" element={<RequireAdmin><AdminAffiliates /></RequireAdmin>} />
            </Routes>
            <AffiliatePop />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
